import { FC, useEffect, useState } from "react";
import GridMultipleSelectorInput from "../../../../../../components/controls/GridMultipleSelectorInput"
import { IEntitySimpleDTO } from "../../../../../../libs/coreapi-dto/@types/common"
import { v4 as uuidv4 } from 'uuid';
import useGridFilter, { CreateSelectorGridFilter } from "../../../../../../system/hooks/useGridFilter"
import { useTranslation } from "react-i18next";
import { DefaultGrid } from "../../../../../../components/grids/default/defaultGrid"
import { ISysOptionsDTO } from "../../../../../../libs/coreapi-dto/service/sysOptions";
import { useAppContext } from "../../../../../../system/providers/appContextProvider";
import { GoodsGroupDataProvider } from "../../../../../../Services/DataProviders/GoodsGroupDataProvider";
import { IGoodsGroupAllViewDTO } from "../../../../../../libs/coreapi-dto/dirs/goodsGroup";
import { GoodsGroupAllSelectorModal } from "../../../../../Dictionaries/GoodsGroup/GoodsGroupAllSelectorModal";
import SysOptionGoodsGroupPluginSettings from "./SysOptionGoodsGroupPluginSettings";
import { FieldsSourceGoodsGroup, ISourceGoodsGroupEditModalProps } from "./interfaces";

const SourceGoodsGroupEditModal: FC<ISourceGoodsGroupEditModalProps> = (props) => {
    const appContext = useAppContext();
    const goodsGroupDP = new GoodsGroupDataProvider(appContext.coreApiService);

    const [selectedGoodsGroup, setSelectedGoodsGroup] = useState<IEntitySimpleDTO[]>([]);
    const [goodsGroupItems, setGoodsGroupItems] = useState<IGoodsGroupAllViewDTO[]>([]);
    const [gridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [gridFilterItems, dispatchGridFilterItems] = useGridFilter(CreateSelectorGridFilter);
    const [totalCount, setTotalCount] = useState(0);
    
    const { t } = useTranslation();
    const baseT = (value: string) => t("sysOptions." + value);

    useEffect(() => {
        if (props.initForGoodsGroup)
            initGoodsGroupItems(props.initForGoodsGroup);
    }, [props.initForGoodsGroup]);

    function getJsonPropByKey(data: ISysOptionsDTO, key: string): any {
        const obj = JSON.parse(data?.jsonData);
        let objValue = obj.Value;
        let items = objValue[key];
        return items;
    }

    function setJsonPropByKey(data: ISysOptionsDTO, key: string, newValue: any): any {
        const obj = JSON.parse(data?.jsonData);
        let objValue = obj.Value;
        objValue[key] = newValue;
        return objValue;
    }

    function initGoodsGroupItems(data: ISysOptionsDTO) {
        const items = getJsonPropByKey(data, FieldsSourceGoodsGroup.ExcludeGroup);
        
        const processGoodsGroupData = (values: IGoodsGroupAllViewDTO[]) => {
            
            let filterValues = values.filter(x => 
                items.some((item: any) => item.GUID === x.idGlobal)
            );
            filterValues = sortGoodsGroupsByName(filterValues);
            
            setGoodsGroupItems(filterValues);
            setTotalCount(filterValues.length);
            
            const selectedItems = mapToEntitySimpleDTO(filterValues);
            setSelectedGoodsGroup(selectedItems);
        };
    
        loadAllGoodsGroups(processGoodsGroupData);
    }
    
    function getGoodsGroupItems(entities: IEntitySimpleDTO[], data?: ISysOptionsDTO) {
        if (!data) return;
       
        const processGoodsGroupData = (values: IGoodsGroupAllViewDTO[]) => {
            let filterValues = values.filter(x => entities.some(entity => entity.idGlobal === x.idGlobal));
            filterValues = sortGoodsGroupsByName(filterValues);
            
            setGoodsGroupItems(filterValues);
            setTotalCount(filterValues.length);
            
            const excludeGroups = extractAndSortNames(filterValues);
            updateSysOptionsData(data, excludeGroups);
        };
    
        loadAllGoodsGroups(processGoodsGroupData);
    }
    
    function sortGoodsGroupsByName(groups: IGoodsGroupAllViewDTO[]): IGoodsGroupAllViewDTO[] {
        return [...groups].sort((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name);
            }
            return 0;
        });
    }
    
    function mapToEntitySimpleDTO(groups: IGoodsGroupAllViewDTO[]): IEntitySimpleDTO[] {
        return groups.map((x) => ({
            displayName: x.name?.toString() || '',
            idGlobal: x.idGlobal
        } as IEntitySimpleDTO));
    }
    
    function extractAndSortNames(groups: IGoodsGroupAllViewDTO[]): any[] {
        return groups.map(group => ({
        //   ID: group.idEsGroupGlobal || group.idGlobal,
          TEXT: group.name || '',
          GUID: group.idGlobal
        }));
      }
    
    function updateSysOptionsData(data: ISysOptionsDTO, excludeGroups: any[]) {
        const objValue = setJsonPropByKey(data, FieldsSourceGoodsGroup.ExcludeGroup, excludeGroups);
        props.setData({
            ...data,
            jsonData: JSON.stringify({ Value: objValue })
        });
    }
    
    function loadAllGoodsGroups(callback: (values: IGoodsGroupAllViewDTO[]) => void) {
        goodsGroupDP.getViewAll(gridFilterItems, (values, totalCount) => {
            if (totalCount > gridFilterItems.numberPerPage) {
                const allDataFilter = { 
                    ...gridFilterItems, 
                    numberPerPage: totalCount, 
                    pageNumber: 1 
                };
                
                goodsGroupDP.getViewAll(allDataFilter, (allValues) => {
                    callback(allValues);
                });
            } else {
                callback(values);
            }
        });
    }

    function updateGoodsGroupItems(entities: IEntitySimpleDTO[]) {
        getGoodsGroupItems(entities, props.data);
    }

    return <>
        <GridMultipleSelectorInput
            selectedEntity={selectedGoodsGroup?.[0]}
            selectedEntities={selectedGoodsGroup}
            id={`idGoodsGroup-${uuidv4()}`}
            switchOnchange
            gridFilter={gridFilter}
            selectorModalJsx={GoodsGroupAllSelectorModal}
            label={baseT('GoodsGroup')}
            onSelect={(entities: IEntitySimpleDTO[]) => {
                setSelectedGoodsGroup(entities);
                updateGoodsGroupItems(entities);
            }}
            disabled={props.lockFromPermission || !props.isEnabled}
            isSearchForMultyselect
        />
        {props.goodsGroupError && (<div style={{ color: 'red', marginBottom: '10px', fontSize: 12 }}>{props.goodsGroupError}</div>)}
        <DefaultGrid
            gridId={uuidv4()}
            data={goodsGroupItems}
            filter={gridFilterItems}
            totalCount={totalCount}
            plugin={SysOptionGoodsGroupPluginSettings}
            selectedItem={undefined}
            onSelect={(row) => { }}
            onSort={(i) => { }}
            onFilterDelete={(i) => { dispatchGridFilterItems({ type: "deleteColumnFilter", payload: i.propertyName }) }}
            onPageNumberChange={(n) => { dispatchGridFilterItems({ type: "changePageNumber", payload: { pageNumber: n } }) }}
            onNumberPerPageChange={(n) => { dispatchGridFilterItems({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
            hiddenPagination={undefined}
            isSearchForMultyselect
        />
    </>
}

export default SourceGoodsGroupEditModal